import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_URL } from '../../config';

function ChannelSettings() {
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [isActive, setIsActive] = useState(true);
  
  useEffect(() => {
    fetchChannels();
  }, []);
  
  const fetchChannels = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/channels`);
      setChannels(response.data.channels);
      setError('');
    } catch (err) {
      console.error('Error fetching channels:', err);
      setError('Failed to load channels');
    } finally {
      setIsLoading(false);
    }
  };
  
  const openEditModal = (channel) => {
    setCurrentChannel(channel);
    setIsActive(channel.is_active === 1);
    setShowEditModal(true);
  };
  
  const handleUpdateChannel = async () => {
    try {
      setError('');
      
      await axios.put(`${API_URL}/channels`, {
        channelId: currentChannel.id,
        isActive
      });
      
      setSuccess('Channel status updated successfully');
      setShowEditModal(false);
      
      // Refresh channels list
      fetchChannels();
    } catch (err) {
      console.error('Error updating channel:', err);
      setError(err.response?.data?.message || 'Error updating channel status');
    }
  };
  
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading channels...</p>
      </div>
    );
  }
  
  return (
    <>
      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="m-0">Channels</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          {channels.length === 0 ? (
            <p className="text-center">No channels found.</p>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {channels.map(channel => (
                  <tr key={channel.id}>
                    <td>{channel.name}</td>
                    <td>
                      <span className={`badge bg-${channel.is_active ? 'success' : 'danger'}`}>
                        {channel.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <Button variant="info" size="sm" onClick={() => openEditModal(channel)}>
                        <FontAwesomeIcon icon={faEdit} /> Edit Status
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      
      {/* Edit Channel Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Channel Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentChannel && (
            <Form>
              <p>Update the status of the {currentChannel.name} channel:</p>
              
              <Form.Group className="mb-3">
                <Form.Check 
                  type="checkbox" 
                  label="Active" 
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              </Form.Group>
              
              <div className="mt-3">
                <p>
                  When a channel is active, users can configure and use it in their accounts.
                  When inactive, users won't see it as an option to configure.
                </p>
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleUpdateChannel}>Update Status</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ChannelSettings;
